/**
 * AuthService — biometric authentication via the oRPC auth router.
 *
 * Flow:
 *  1. auth.requestBiometric   — sends push notification to iVALT mobile app
 *  2. auth.pollBiometricResult — polls until approved / denied / timeout
 *  3. auth.createSession       — exchanges the biometric result for a session cookie
 *
 * Session is managed via HttpOnly cookie set by the web app; the add-in
 * passes credentials: 'include' on every request via addinClient.
 */

import {
  decryptPrivateKey,
  decryptVaultPayload,
  generateEphemeralKeyPair,
  importPrivateKeyFromPem,
} from '@docuid/crypto-client';

import type { StoredAuth } from '../common/types';
import { addinClient } from './addinClient';
import { logger } from './Logger';

const authLogger = logger.createContextLogger('AuthService');

// Local-storage key for add-in session cache (user info only; token lives in cookie).
const STORAGE_KEY = 'docuid_auth';

export class AuthService {
  // In-memory cache for the decrypted user private key.
  // Never written to disk or localStorage for security reasons.
  private static userPrivateKey: CryptoKey | null = null;
  private static userPublicKeyPem: string | null = null;

  private static isDemoNumber(phone: string): boolean {
    // Demo mode is gated by a single exact phone number so user input
    // cannot bypass the biometric flow via trailing-digit matches or country patterns.
    // The number is matched as bare digits, so the "+91" prefix added by PhoneInput
    // is stripped before comparison.
    const digitsOnly = phone.replaceAll(/\D/g, '');
    return digitsOnly === '919876543210' || digitsOnly === '9876543210';
  }

  static isDemoMode(): boolean {
    return AuthService.getSessionToken() === 'demo-session-token';
  }

  /**
   * Get the decrypted user private key from memory.
   */
  public static getUserPrivateKey(): CryptoKey | null {
    return AuthService.userPrivateKey;
  }

  /**
   * Get the user's RSA public key PEM from memory.
   */
  public static getUserPublicKeyPem(): string | null {
    return AuthService.userPublicKeyPem;
  }

  /**
   * Authenticate user with phone number via biometric verification.
   * On success the web app sets a session cookie; user info is cached in localStorage.
   */
  static async login(phoneNumber: string): Promise<void> {
    try {
      authLogger.info('Starting biometric authentication', {
        phoneNumber:
          phoneNumber.slice(0, 3) + '***' + phoneNumber.slice(Math.max(0, phoneNumber.length - 3)),
      });

      if (AuthService.isDemoNumber(phoneNumber)) {
        authLogger.info('Demo number detected. Bypassing biometric authentication.');

        const authData: StoredAuth = {
          phone: phoneNumber,
          sessionToken: 'demo-session-token',
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
          user: {
            id: 999_999,
            name: 'Demo User',
            email: 'demo@docuid.net',
            mobile: phoneNumber,
            country_code: '91',
            address: 'Demo Location',
            imei: 'demo-device-imei',
          },
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));

        authLogger.info('Authentication successful (Demo Mode)', {
          userId: authData.user?.id,
          userName: authData.user?.name,
        });

        authLogger.logAuthEvent('LOGIN_SUCCESS', authData.user?.id?.toString() || '');
        return;
      }

      // Generate the ephemeral keypair for ZK vault secret exchange
      const keyPair = await generateEphemeralKeyPair();
      const clientPublicKey = keyPair.publicKeyPem;

      // Step 1: request biometric push
      const started = await addinClient.auth.requestBiometric({
        mobile: phoneNumber,
        clientPublicKey,
      });
      if (started.status === 'not_found') {
        throw new Error(
          'No iVALT account is registered for this number. Install the iVALT app and register, then sign in here.',
        );
      }

      authLogger.info('Biometric request sent — waiting for user approval');

      // Step 2: poll for result
      const authResult = await AuthService.pollBiometricResult(phoneNumber);

      // Step 3: create session (sets HttpOnly cookie).
      // Must happen before encryptionKeys.getMine — that procedure requires
      // the session cookie and returns 401 when called unauthenticated.
      const sessionResult = await addinClient.auth.createSession({
        mobile: phoneNumber,
        ivaltUserId: authResult.user?.id,
      });

      const user = sessionResult.user;

      // Step 4: decrypt vault secret and load the user private key if payload is present
      if (authResult.encryptedVaultPayload) {
        authLogger.info('Decrypting vault secret locally...');
        const vaultSecret = await decryptVaultPayload(
          authResult.encryptedVaultPayload,
          keyPair.privateKey,
        );

        authLogger.info('Retrieving and decrypting user private key bundle...');
        const keysBundle = await addinClient.encryptionKeys.getMine();

        const privateKeyPem = await decryptPrivateKey(
          {
            encryptedPrivateKey: keysBundle.encryptedPrivateKey,
            iv: keysBundle.privateKeyIv,
            salt: keysBundle.privateKeySalt,
          },
          vaultSecret,
        );
        AuthService.userPrivateKey = await importPrivateKeyFromPem(privateKeyPem);
        AuthService.userPublicKeyPem = keysBundle.publicKeyPem;
        authLogger.info('User private key successfully loaded in memory');
      }

      // Cache user info locally so the UI can show name/email without extra calls
      const authData: StoredAuth = {
        phone: phoneNumber,
        sessionToken: 'cookie', // Actual token is HttpOnly cookie, not accessible to JS
        expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24h pessimistic; server may extend
        user: {
          id: parseInt(user.id, 10) || 0,
          name: user.name,
          email: user.email,
          mobile: user.mobileNumber ?? phoneNumber,
          country_code: '',
          address: '',
        },
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));

      authLogger.info('Authentication successful', {
        userId: user.id,
        userName: user.name,
      });

      authLogger.logAuthEvent('LOGIN_SUCCESS', user.id);
    } catch (error) {
      authLogger.error('Authentication failed', error as Error);
      AuthService.userPrivateKey = null;
      throw error;
    }
  }

  /**
   * Poll the oRPC auth.pollBiometricResult procedure until the user
   * approves or denies the biometric prompt, or the timeout is reached.
   */
  private static async pollBiometricResult(mobile: string): Promise<{
    user?: { id: string; name: string; email: string; mobileNumber: string | null };
    encryptedVaultPayload?: string;
  }> {
    const maxAttempts = 60;
    const pollInterval = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = await addinClient.auth.pollBiometricResult({ mobile });

      if (result.status === 'success') {
        return {
          user: result.user as {
            id: string;
            name: string;
            email: string;
            mobileNumber: string | null;
          },
          encryptedVaultPayload: result.encryptedVaultPayload,
        };
      }

      if (result.status === 'denied') {
        throw new Error('Biometric authentication was denied. Please try again.');
      }

      if (result.status === 'not_found') {
        throw new Error(
          'No iVALT account is registered for this number. Install the iVALT app and register, then sign in here.',
        );
      }

      // status === 'pending' — keep polling
      authLogger.info(`Polling attempt ${attempt + 1}: pending`);
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    throw new Error('Authentication timed out. Please try again.');
  }

  /**
   * Get stored authentication data from localStorage.
   * Note: the session cookie is validated server-side; this is just for UI state.
   */
  static getStoredAuth(): StoredAuth | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const authData: StoredAuth = JSON.parse(stored);

      if (Date.now() > authData.expiresAt) {
        AuthService.logout();
        return null;
      }

      return authData;
    } catch {
      return null;
    }
  }

  /**
   * Get the session token.
   * Always returns a truthy string when logged in (the actual token is a cookie).
   */
  static getSessionToken(): string | null {
    const auth = AuthService.getStoredAuth();
    return auth ? auth.sessionToken : null;
  }

  /**
   * Clear local session cache and call the oRPC logout procedure to
   * invalidate the server-side session and clear the cookie.
   */
  static async logoutAsync(): Promise<void> {
    try {
      if (!AuthService.isDemoMode()) {
        await addinClient.auth.logout({});
      }
    } catch (error) {
      authLogger.warn('Logout RPC failed (session may already be invalid)', { error });
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      AuthService.userPrivateKey = null;
      AuthService.userPublicKeyPem = null;
      authLogger.info('User logged out');
    }
  }

  /**
   * Synchronous logout — clears localStorage only.
   * Use logoutAsync() when possible to also invalidate the server session.
   */
  static logout(): void {
    localStorage.removeItem(STORAGE_KEY);
    AuthService.userPrivateKey = null;
    AuthService.userPublicKeyPem = null;
    authLogger.info('User logged out (sync)');
  }

  /**
   * Check if user is currently authenticated (local cache only).
   */
  static isAuthenticated(): boolean {
    return AuthService.getStoredAuth() !== null;
  }
}
